<template>
  <div class="renew-page">
    <v-row justify="center">
      <v-col cols="12" sm="8" md="6" lg="5">
        <v-card class="renew-card" flat>
          <!-- HEADER BAND -->
          <div class="renew-header pl-base-bg">
            <div class="renew-header-icon">
              <v-icon color="#fff" size="22">mdi-credit-card-check-outline</v-icon>
            </div>
            <div>
              <div class="renew-title">Renew Subscription</div>
              <div class="renew-subtitle">Keep your ProfitLedgex account active</div>
            </div>
          </div>

          <div class="pa-6">
            <!-- LOADING -->
            <div v-if="state === 'loading'" class="renew-center py-8">
              <v-progress-circular indeterminate color="#0a5a90" size="36" />
            </div>

            <!-- NEEDS LOGIN FALLBACK (no/expired token) -->
            <div v-else-if="state === 'needsLogin'">
              <v-alert type="warning" dense outlined class="mb-4">
                {{ fallbackMessage }}
              </v-alert>

              <v-form ref="fallbackForm" @submit.prevent="fallbackLogin">
                <v-text-field
                  v-model.trim="fallbackEmail"
                  label="Email address"
                  outlined
                  dense
                  type="email"
                  prepend-inner-icon="mdi-email-outline"
                  :rules="[(v) => !!v || 'Required']"
                />
                <v-text-field
                  v-model="fallbackPassword"
                  label="Password"
                  outlined
                  dense
                  type="password"
                  prepend-inner-icon="mdi-lock-outline"
                  :rules="[(v) => !!v || 'Required']"
                />

                <v-alert v-if="fallbackError" type="error" dense outlined class="mb-3">
                  {{ fallbackError }}
                </v-alert>

                <v-btn
                  block
                  class="renew-action-btn"
                  :loading="fallbackLoading"
                  type="submit"
                >
                  Continue
                </v-btn>
              </v-form>
            </div>

            <!-- READY TO PAY / PAYING -->
            <div v-else-if="['ready', 'ordering', 'verifying'].includes(state)">
              <div class="renew-summary">
                <div class="renew-summary-row">
                  <span>Name</span>
                  <strong>{{ identifiedUser.name }}</strong>
                </div>
                <div class="renew-summary-row">
                  <span>Email</span>
                  <strong>{{ identifiedUser.email }}</strong>
                </div>
                <div class="renew-summary-row">
                  <span>Current expiry</span>
                  <strong>{{ formattedExpiry }}</strong>
                </div>
              </div>

              <v-divider class="my-4" />

              <div class="renew-plan-options">
                <div
                  v-for="plan in config.plans"
                  :key="plan.id"
                  class="renew-plan-option"
                  :class="{ 'renew-plan-option--selected': selectedPlanId === plan.id }"
                  @click="selectedPlanId = plan.id"
                >
                  <div>
                    <div class="renew-plan-label">{{ plan.label }}</div>
                  </div>
                  <strong>₹{{ plan.amount }}</strong>
                </div>
              </div>

              <v-alert v-if="payError" type="error" dense outlined class="mt-4 mb-0">
                {{ payError }}
              </v-alert>

              <v-btn
                block
                class="renew-action-btn mt-5"
                :disabled="!selectedPlanId"
                :loading="state === 'ordering' || state === 'verifying'"
                @click="startPayment"
              >
                Pay ₹{{ selectedPlanAmount }} to Renew
              </v-btn>

              <v-btn
                v-if="lastOrderId"
                block
                text
                small
                class="mt-2"
                :loading="checkingStatus"
                @click="checkStatus"
              >
                Already paid? Check status
              </v-btn>
            </div>

            <!-- SUCCESS -->
            <div v-else-if="state === 'success'" class="renew-center py-4">
              <v-icon size="56" color="#16a34a">mdi-check-circle-outline</v-icon>
              <div class="renew-success-title mt-3">Subscription renewed!</div>
              <div class="renew-success-sub mt-1">
                Your subscription is now valid until <strong>{{ formattedNewExpiry }}</strong>.
              </div>
              <v-btn block class="renew-action-btn mt-5" @click="$router.push('/login')">
                Go to Login
              </v-btn>
            </div>

            <!-- FAILURE -->
            <div v-else-if="state === 'failure'" class="renew-center py-4">
              <v-icon size="56" color="#dc2626">mdi-close-circle-outline</v-icon>
              <div class="renew-success-title mt-3">Payment not completed</div>
              <div class="renew-success-sub mt-1">{{ failureMessage }}</div>
              <v-btn block class="renew-action-btn mt-5" @click="state = 'ready'">
                Try Again
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { openRazorpayCheckout } from "@/utils/razorpay-checkout";

export default {
  name: "RenewSubscription",

  data() {
    return {
      state: "loading",
      token: null,

      config: { keyId: "", plans: [], currency: "INR" },
      selectedPlanId: null,
      identifiedUser: { name: "", email: "", paymentExpiryDate: null },

      lastOrderId: null,
      newExpiryDate: null,
      payError: "",
      failureMessage: "",
      checkingStatus: false,

      fallbackMessage: "This renewal link has expired or is invalid. Please log in to continue.",
      fallbackEmail: "",
      fallbackPassword: "",
      fallbackError: "",
      fallbackLoading: false,
    };
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated", "user"]),

    formattedExpiry() {
      return this.identifiedUser.paymentExpiryDate
        ? new Date(this.identifiedUser.paymentExpiryDate).toLocaleDateString("en-IN", { dateStyle: "medium" })
        : "—";
    },

    formattedNewExpiry() {
      return this.newExpiryDate
        ? new Date(this.newExpiryDate).toLocaleDateString("en-IN", { dateStyle: "medium" })
        : "—";
    },

    selectedPlanAmount() {
      const plan = this.config.plans.find((p) => p.id === this.selectedPlanId);
      return plan ? plan.amount : 0;
    },
  },

  async mounted() {
    this.token = this.$route.query.token || null;
    await this.init();
  },

  methods: {
    ...mapActions("renewal", ["fetchConfig", "identify", "createOrder", "verifyPayment", "checkOrderStatus"]),

    async init() {
      try {
        this.config = await this.fetchConfig();
        this.selectedPlanId = this.config.defaultPlanId || this.config.plans?.[0]?.id || null;
      } catch (err) {
        console.error(err);
      }

      // Still-logged-in user (proactive renewal) — identify from session,
      // no token needed.
      if (!this.token && this.isAuthenticated && this.user) {
        this.identifiedUser = {
          name: this.user.name,
          email: this.user.email,
          paymentExpiryDate: this.user.paymentExpiryDate,
        };
        this.state = "ready";
        return;
      }

      if (!this.token) {
        this.state = "needsLogin";
        return;
      }

      try {
        this.identifiedUser = await this.identify(this.token);
        this.state = "ready";
      } catch (err) {
        this.fallbackMessage =
          err.response?.data?.message ||
          "This renewal link has expired or is invalid. Please log in to continue.";
        this.state = "needsLogin";
      }
    },

    async fallbackLogin() {
      if (!this.$refs.fallbackForm.validate()) return;
      this.fallbackError = "";
      this.fallbackLoading = true;
      try {
        await this.$store.dispatch("auth/login", {
          email: this.fallbackEmail.toLowerCase(),
          password: this.fallbackPassword,
        });
        // Login succeeded outright — payment must be fine, send them in.
        this.$router.push("/dashboard");
      } catch (error) {
        const data = error.response?.data;
        if (data?.code === "PAYMENT_EXPIRED" && data?.renewalToken) {
          this.token = data.renewalToken;
          await this.init();
        } else {
          this.fallbackError = data?.message || "Invalid email or password";
        }
      } finally {
        this.fallbackLoading = false;
      }
    },

    async startPayment() {
      this.payError = "";
      this.state = "ordering";
      try {
        const order = await this.createOrder({ token: this.token, planId: this.selectedPlanId });
        this.lastOrderId = order.orderId;

        openRazorpayCheckout({
          order,
          config: this.config,
          prefillEmail: this.identifiedUser.email,
          onSuccess: (response) => this.handlePaymentSuccess(response),
          onDismiss: () => {
            if (this.state === "ordering") this.state = "ready";
          },
        });

        this.state = "ready";
      } catch (err) {
        console.error(err);
        this.payError = err.response?.data?.message || "Failed to start payment. Please try again.";
        this.state = "ready";
      }
    },

    async handlePaymentSuccess(response) {
      this.state = "verifying";
      try {
        const result = await this.verifyPayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        this.newExpiryDate = result.paymentExpiryDate;
        this.state = "success";
      } catch (err) {
        console.error(err);
        this.failureMessage =
          err.response?.data?.message || "We couldn't verify your payment. Please check status or try again.";
        this.state = "failure";
      }
    },

    async checkStatus() {
      if (!this.lastOrderId) return;
      this.checkingStatus = true;
      try {
        const result = await this.checkOrderStatus(this.lastOrderId);
        if (result.status === "verified") {
          this.newExpiryDate = result.newExpiryDate;
          this.state = "success";
        } else {
          this.payError = "Payment not confirmed yet. If you completed the payment, please wait a moment and try again.";
        }
      } catch (err) {
        console.error(err);
        this.payError = "Failed to check payment status.";
      } finally {
        this.checkingStatus = false;
      }
    },
  },
};
</script>

<style scoped>
.renew-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: #f4f6f9;
  padding: 32px 16px;
}

.renew-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.renew-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.renew-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.renew-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.renew-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.renew-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.renew-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #374151;
  padding: 4px 0;
}

.renew-plan-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.renew-plan-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 0;
  cursor: pointer;
  font-size: 15px;
  color: #0f172a;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.renew-plan-option:hover {
  border-color: #0a5a90;
}

.renew-plan-option--selected {
  border-color: #0a5a90;
  background: rgba(10, 90, 144, 0.06);
}

.renew-plan-label {
  font-weight: 600;
}

.renew-action-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.renew-success-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.renew-success-sub {
  font-size: 14px;
  color: #64747f;
}
</style>
